"use client";

import { useRouter } from "next/navigation";
import { FC, Suspense, useState } from "react";
import { AxiosError, HttpStatusCode } from "axios";
import {
  AppRouterInstance,
} from "next/dist/shared/lib/app-router-context.shared-runtime";
import { LogsPublicApi } from "@datadog/browser-logs/cjs/boot/logsPublicApi";
import { useAPI } from "apps/website/hooks/useAPI";
import { ApiClientsContext } from "apps/website/contexts/api";
import { useDatadog } from "libs/auth/client-sdk-react/src/useDatadog";

import { DisplayState } from "@/constants/state";

import SectionStickyCTA from "../SectionStickyCTA/SectionStickyCTA";
import { AlertPrompt } from "../../feature/AlertPrompt/AlertPrompt";
import Input from "../../form/Input/Input";
import Spacer from "../../layout/Spacer/Spacer";

export interface ValidateDiscountButtonsProps {
  discountCode?: string;
}

const handleError = (
  error: unknown,
  setError: (error: string) => void,
) => {
  if (!(error instanceof AxiosError)) {
    setError("An error occurred");
    return;
  }

  switch (error.response?.status) {
    case HttpStatusCode.BadRequest:
    case HttpStatusCode.NotFound:
      setError("Please enter a valid code");
      break;
    case HttpStatusCode.Conflict:
      setError("This code has been used");
      break;
    default:
      setError("An error occurred");
  }
};

const validateDiscount = (
  setButtonState: (state: DisplayState) => void,
  setError: (state: string) => void,
  setIsDisabled: (isDisabled: boolean) => void,
  api: ApiClientsContext,
  router: AppRouterInstance,
  discountCode: string,
  datadog: LogsPublicApi,
) => async () => {
  setButtonState(DisplayState.LOADING);

  try {
    const result = await api.Discounts.validateInfluencerCode(discountCode.trim());

    if (!result.data.success) {
      datadog.logger.error("Error validating discount code", {
        discountCode,
        error: "Invalid discount code",
        result,
      });
      setError("Something went wrong, please try again.");
      setIsDisabled(true);
      return;
    }

    router.push(`/forms/fresh-new-user-influencer?discount_code=${discountCode}`);
  } catch (error) {
    datadog.logger.error("Error validating discount code", {
      discountCode,
      error,
    });
    handleError(error, setError);
    setIsDisabled(true);
    setButtonState(DisplayState.READY);
  }
};

const EnterDiscountSection: FC<ValidateDiscountButtonsProps> = () => {

  const api = useAPI();
  const router = useRouter();
  const [ error, setError ] = useState<string | null>(null);

  const [ isDisabled, setIsDisabled ] = useState(true);

  const [ buttonState, setButtonState ] = useState<DisplayState>(
    DisplayState.READY,
  );
  const [ discountCode, setDiscountCode ] = useState("");
  const datadog = useDatadog();

  return (
    <>
      <Input
        name="discountCode"
        label="MY FREE BOX CODE IS"
        onChange={(event) => {
          setIsDisabled(!event.target.value);
          setDiscountCode(event.target.value);
          setError(null);
        }}
      />
      <Spacer size="md"></Spacer>
      <Suspense>
        <SectionStickyCTA
          cta={{ name: "Continue", id: "continue", to: "" }}
          showTrustPilotRating={false}
          disabled={isDisabled}
          onClick={validateDiscount(setButtonState, setError, setIsDisabled, api, router, discountCode, datadog)}
          state={buttonState}>
        </SectionStickyCTA >
      </Suspense>
      { error !== null &&
        <AlertPrompt hideImage={true} style="warning" title="">{ error }</AlertPrompt>
      }
    </>
  );
};

export default EnterDiscountSection;
